/**
 * Links institucionais e canais de atendimento — fonte única de verdade.
 *
 * Os links institucionais são consumidos por dois lugares que precisam mostrar
 * exatamente as mesmas opções: o rodapé (src/components/Footer.jsx) e o menu
 * sanduíche mobile (src/components/NavBar.jsx). No mobile o rodapé só aparece na
 * Home, então o menu é o único caminho para Termos/Privacidade/Cookies nas demais
 * telas — duplicar as listas faria as duas divergirem no primeiro ajuste de copy.
 * Os canais de atendimento hoje só o rodapé percorre: a gaveta "Ajuda" do menu
 * deixou de listá-los e aponta para o formulário do Quem somos, onde eles estão.
 * As redes sociais aparecem nos dois lugares que têm um bloco de contato — o rodapé
 * e a seção "Contato" do Quem somos — via src/components/RedesSociais.jsx.
 *
 * Os ícones ficam de fora de propósito: cada consumidor resolve o ícone pelo
 * `id` do canal. Este módulo é dado, não apresentação — não importa react-icons.
 */

/**
 * Ordem de exibição, igual nos dois consumidores: a coluna "Ajuda e condições" do rodapé
 * e a gaveta "Ajuda" do menu sanduíche.
 *
 * Abre com o que se procura com pressa — o passo a passo (Central de ajuda) e a dúvida (FAQ),
 * depois as condições e as políticas — e fecha com "Quem somos", que é institucional e ninguém
 * abre o menu para encontrar. Aqui, e não no NavBar: o menu chegou a carregar uma ordem própria, e duas
 * listas descrevendo a mesma coisa divergem no primeiro ajuste de qualquer uma delas.
 */
export const linksInstitucionais = [
  { to: "/ajuda", label: "Central de ajuda" },
  { to: "/perguntas-frequentes", label: "Perguntas frequentes" },
  { to: "/termos-de-uso", label: "Termos de Uso" },
  { to: "/politica-de-privacidade", label: "Política de Privacidade" },
  { to: "/politica-de-cookies", label: "Política de Cookies" },
  // Exigência de listagem da Play Store: a URL de solicitação de exclusão precisa ser alcançável
  // de fora do app, sem login. Aqui ela fica nos dois lugares que já listam as condições -- rodapé
  // e menu sanduíche -- e não num link solto que só o console da loja conhece.
  //
  // `soNaWeb`: o requisito é justamente a URL de FORA do app; dentro dele, quem tem conta já
  // a exclui pelo Meu cadastro, e quem não entrou não tem o que excluir. Cada consumidor filtra
  // com `ehNativo()` -- este módulo é dado e não pergunta pela plataforma.
  { to: "/excluir-conta", label: "Excluir minha conta", soNaWeb: true },
  { to: "/quemsomos", label: "Quem somos" },
];

/**
 * O formulário de contato do Quem somos. Fora de `linksInstitucionais` de propósito: aquele
 * array é o que rodapé e menu mostram *iguais*, e este item cada um põe num lugar diferente —
 * o rodapé como terceiro canal de "Atendimento", o sanduíche no fim da gaveta "Ajuda", o header
 * de desktop como item de destaque de profissional e visitante. O destino e o rótulo, esses são
 * um só.
 */
export const formularioContato = { to: "/quemsomos#contato", label: "Fale conosco" };

/**
 * Canais de atendimento, na ordem de prioridade (WhatsApp primeiro).
 *
 * `externo: true` marca o que precisa abrir em nova aba — `mailto:` não deve,
 * senão o navegador deixa uma aba em branco para trás.
 *
 * `nome` nomeia o canal para quem monta um nome acessível ("WhatsApp: (43) …"):
 * o `label` sozinho (um número de telefone, um endereço de e-mail) não diz o que
 * ele é. Nasceu para a gaveta do menu mobile, que já não lista os canais; hoje é o
 * rodapé que precisa dele, porque mostra os dois só como ícone.
 */
export const canaisAtendimento = [
  {
    id: "whatsapp",
    nome: "WhatsApp",
    href: "https://wa.me/5543999786054",
    label: "(43) 99978-6054",
    complemento: "(somente mensagem)",
    externo: true,
  },
  {
    id: "email",
    nome: "E-mail",
    href: "mailto:atendimento@novafaxina.com.br",
    label: "atendimento@novafaxina.com.br",
  },
];

/**
 * Perfis da Nova Faxina nas redes. Não entram em `canaisAtendimento` porque não são um
 * canal para falar com a equipe — são presença — e quem os mostra desenha só o logotipo,
 * sem número ou endereço embaixo. O `nome` é o nome acessível do link.
 */
export const redesSociais = [
  {
    id: "facebook",
    nome: "Facebook",
    href: "https://www.facebook.com/profile.php?id=61593766573085",
  },
  {
    id: "instagram",
    nome: "Instagram",
    href: "https://www.instagram.com/novafaxina",
  },
  {
    id: "linkedin",
    nome: "LinkedIn",
    href: "https://www.linkedin.com/company/novafaxina",
  },
  {
    id: "youtube",
    nome: "YouTube",
    href: "https://www.youtube.com/@novafaxina",
  },
];
